import * as React from "react";
import {
  ApiError,
  type PaymentEvent,
  type PaymentReceived,
  type PaymentRecordInput,
  type PaymentStatus,
  type ReconcileEnrolment,
} from "@alight/contracts";
import {
  Badge,
  type BadgeProps,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import { CheckCircle2, Loader2 } from "lucide-react";

/* Shared building blocks for the two reconciliation surfaces: the account
 * manager's /reconcile pages (magic-link session) and the host portal's
 * /admin/reconciliation (Cloudflare Access). Both talk to the same handlers,
 * only via different URL prefixes. */

export function formatAud(cents: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

export const RECON_STATUS_BADGE: Record<PaymentStatus, BadgeProps["variant"]> = {
  unpaid: "muted",
  pending_review: "accent",
  partial: "accent",
  paid: "success",
  refunded: "outline",
  cancelled: "outline",
};

export const RECON_STATUS_LABEL: Record<PaymentStatus, string> = {
  unpaid: "Unpaid",
  pending_review: "Pending review",
  partial: "Partially paid",
  paid: "Paid",
  refunded: "Refunded",
  cancelled: "Cancelled",
};

/* `payid` is the journey's legacy code for the bank-transfer option — parents
 * are shown plain BSB/account details, so it must read "Bank transfer" too. */
export const METHOD_LABEL: Record<string, string> = {
  payid: "Bank transfer",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  stripe: "Card",
  already: "Already paid (claimed)",
  other: "Other",
};

/** Methods a reconciler can record money under. */
const RECORDABLE_METHODS = ["bank_transfer", "cash", "other"] as const;

export interface RecordPaymentDialogProps {
  enrolment: ReconcileEnrolment | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: PaymentRecordInput) => void;
  pending: boolean;
  error: unknown;
}

/**
 * "Record payment received" — the one dialog for both partial and full
 * amounts. Defaults to the outstanding balance; entering less records a part
 * payment (status becomes `partial`), the full balance settles it (`paid`).
 */
export function RecordPaymentDialog({
  enrolment,
  onOpenChange,
  onSubmit,
  pending,
  error,
}: RecordPaymentDialogProps) {
  const [amount, setAmount] = React.useState("");
  const [method, setMethod] = React.useState<string>("bank_transfer");
  const [note, setNote] = React.useState("");
  /* Emailing the family is the DEFAULT and re-arms itself for every record —
     silence is a deliberate choice about one payment, never a mode you can
     leave switched on and forget. */
  const [notify, setNotify] = React.useState(true);

  // Re-seed the form whenever a new record is opened.
  const balance = enrolment ? Math.max(enrolment.balance_cents, 0) : 0;
  React.useEffect(() => {
    if (!enrolment) return;
    setAmount((Math.max(enrolment.balance_cents, 0) / 100).toFixed(2));
    // `payid` (the journey's bank-transfer code) seeds as bank_transfer.
    setMethod(enrolment.payment_method === "cash" ? "cash" : "bank_transfer");
    setNote("");
    setNotify(true);
    /* Keyed on "is it open" as well as which record: closing and re-opening
       the SAME row must re-arm the email, or an unticked box survives into a
       payment the host did mean to tell the family about. */
  }, [enrolment?.id, enrolment !== null]);

  const amountCents = Math.round(Number(amount) * 100);
  const amountValid = Number.isFinite(amountCents) && amountCents > 0;
  const willSettle = amountValid && amountCents >= balance;

  return (
    <Dialog open={enrolment !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record payment received</DialogTitle>
          <DialogDescription>
            {enrolment ? (
              <>
                <strong className="text-ink">{enrolment.payment_ref}</strong> —{" "}
                {enrolment.parent_name} ({enrolment.child_name}). Fee{" "}
                {formatAud(enrolment.amount_cents)}
                {enrolment.received_cents > 0 && (
                  <>
                    , received so far {formatAud(enrolment.received_cents)}
                  </>
                )}
                . Outstanding <strong className="text-ink">{formatAud(balance)}</strong>.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="recon-amount">Amount received (AUD)</Label>
            <Input
              id="recon-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={!amountValid}
            />
            <p className="text-xs text-muted">
              {willSettle
                ? notify
                  ? "This clears the balance — the record becomes Paid and the parent is emailed a confirmation."
                  : "This clears the balance — the record becomes Paid, quietly."
                : notify
                  ? "Less than the balance records a part payment — the parent is emailed a receipt with the remainder."
                  : "Less than the balance records a part payment, quietly."}
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="recon-method">How it arrived</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="recon-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORDABLE_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {METHOD_LABEL[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="recon-note">Note (optional)</Label>
            <Textarea
              id="recon-note"
              rows={2}
              placeholder="e.g. matched transfer from J. Smith 12 Jul"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* The safety catch. Recording money normally tells the family; when
              the money is being written down after the fact and they already
              know, a receipt dated today reads as a second charge. */}
          <div className="rounded-card border border-border bg-paper p-3">
            <label
              className="flex cursor-pointer items-start gap-2 text-sm font-medium text-ink"
              htmlFor="recon-notify"
            >
              <input
                id="recon-notify"
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              <span>
                Email {enrolment?.parent_name ? enrolment.parent_name : "the parent"}{" "}
                {willSettle ? "a confirmation" : "a receipt"}
              </span>
            </label>
            {!notify && (
              <p className="mt-1.5 pl-6 text-xs text-muted">
                Nothing will be sent. The payment is still recorded and still shows in
                the history — use this when they already know it&apos;s been paid.
              </p>
            )}
          </div>
        </div>

        {Boolean(error) && (
          <p
            className="rounded-card border border-border bg-paper p-3 text-sm text-ink"
            role="alert"
          >
            Couldn&apos;t record that payment
            {error instanceof ApiError ? ` (error ${error.status})` : ""}. Please try
            again.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            disabled={pending || !amountValid}
            onClick={() =>
              onSubmit({
                amount_cents: amountCents,
                method: method as PaymentRecordInput["method"],
                ...(note.trim() ? { note: note.trim() } : {}),
                // Only sent when it's false: the default stays exactly the
                // request every caller made before the catch existed.
                ...(notify ? {} : { notify: false }),
              })
            }
          >
            {pending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Recording…
              </>
            ) : willSettle ? (
              notify ? (
                "Record & mark paid"
              ) : (
                "Record quietly & mark paid"
              )
            ) : notify ? (
              "Record part payment"
            ) : (
              "Record part payment quietly"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Opening words for a payment request — a starting point, not a template.
 *
 * The host edits this before it sends, because the reason a registration is
 * unpaid is never the same twice (a leader fee that was missed, a transfer
 * that never arrived, a family who thought someone else had paid). A fixed
 * wording would be wrong more often than right, so this one is deliberately
 * mild and short enough to rewrite.
 */
export function defaultRequestMessage(childName: string, owedCents: number): string {
  return (
    `We don't have a record of payment for ${childName}'s registration yet — ` +
    `there's ${formatAud(Math.max(owedCents, 0))} outstanding.\n\n` +
    `Could you please arrange it when you get a chance? If you've already paid, ` +
    `just reply and let us know so we can match it up.`
  );
}

/**
 * The same opening, addressed to a FAMILY rather than one child.
 *
 * It deliberately does not name the children: the email itemises them
 * underneath, settled ones included, and repeating the list in the host's
 * words is how the two end up disagreeing after a payment lands between
 * writing and sending.
 */
export function defaultFamilyRequestMessage(count: number, owedCents: number): string {
  return (
    `We're still missing payment for ${count} of your registrations — ` +
    `${formatAud(Math.max(owedCents, 0))} in total.\n\n` +
    `The full list is below, including anything already paid. Could you please ` +
    `arrange the balance when you get a chance? One payment for the lot is ` +
    `fine. If you've already sent it, just reply and let us know so we can ` +
    `match it up.`
  );
}

export interface FeeCorrection {
  amount_cents: number;
  reason?: string;
}

/** Who the chase is addressed to. "family" widens it to every registration
 *  under the same parent address that still owes money — the settled ones ride
 *  along on the email, marked, but are never charged for. */
export type RequestScope = "one" | "family";

/** The rest of this family's outstanding registrations, as counted from the
 *  list already on screen. Absent (or `count` 0) when this child is the only
 *  one owing, in which case the dialog offers no choice at all — a scope
 *  picker with one real option is noise. */
export interface FamilyScope {
  /** How many registrations owe money, INCLUDING the one being viewed. */
  count: number;
  /** What they owe between them. */
  totalCents: number;
}

export interface RequestPaymentDialogProps {
  enrolment: ReconcileEnrolment | null;
  /** Siblings also owing — see FamilyScope. */
  family?: FamilyScope | null;
  onOpenChange: (open: boolean) => void;
  /** Correct the fee first if one is supplied, then email the family. */
  onSend: (input: { message: string; fee?: FeeCorrection; scope: RequestScope }) => void;
  /** Correct the fee and send nothing at all. */
  onSaveFee: (fee: FeeCorrection) => void;
  pending: boolean;
  /** The email failed. */
  error: unknown;
  /** The fee change failed — nothing was emailed. */
  feeError: unknown;
}

/**
 * "Ask for payment" — one family, in the host's own words.
 *
 * It also holds the FEE, because "they owe $25 and the system thinks they
 * don't" is one thought, not two. A record reads as settled whenever nothing
 * was ever charged — a leader-fee waiver or family discount that should not
 * have applied — and until the fee is corrected there is no balance to ask
 * for. So the dialog opens on the fee when nothing is owing, recalculates the
 * outstanding amount as it is typed, and saves it before the email goes.
 *
 * What the host CANNOT get wrong here: the recipient, the amount quoted, the
 * reference and the Pay now link all come from the record on the server.
 */
export function RequestPaymentDialog({
  enrolment,
  family,
  onOpenChange,
  onSend,
  onSaveFee,
  pending,
  error,
  feeError,
}: RequestPaymentDialogProps) {
  const [message, setMessage] = React.useState("");
  const [scope, setScope] = React.useState<RequestScope>("one");
  const [feeOpen, setFeeOpen] = React.useState(false);
  const [feeAmount, setFeeAmount] = React.useState("");
  const [feeReason, setFeeReason] = React.useState("");
  /* The wording we suggested last: the host owns the message the moment they
     touch it, so a fee change only rewrites words they haven't changed. */
  const suggested = React.useRef("");

  React.useEffect(() => {
    if (!enrolment) return;
    const owed = Math.max(enrolment.amount_cents - enrolment.received_cents, 0);
    const opening = defaultRequestMessage(enrolment.child_name, owed);
    suggested.current = opening;
    setMessage(opening);
    setFeeAmount((enrolment.amount_cents / 100).toFixed(2));
    setFeeReason("");
    /* Opens on the SINGLE child every time, even when the family has others
       owing. Chasing one child is the narrower, safer act, and a dialog that
       silently defaulted to emailing about five registrations would eventually
       send one the host didn't mean. They opt in. */
    setScope("one");
    // Nothing owing means the FEE is what's wrong — open on it, rather than
    // making the host find a "the fee is wrong" link before they can act.
    setFeeOpen(owed <= 0);
    /* Also keyed on "is it open", not just which record: closing and
       re-opening the SAME row must start clean, or a fee typed and then
       abandoned is still sitting in the box the next time it is opened. */
  }, [enrolment?.id, enrolment !== null]);

  const feeCents = Math.round(Number(feeAmount) * 100);
  const feeValid = Number.isFinite(feeCents) && feeCents >= 0;
  const feeChanged = enrolment !== null && feeValid && feeCents !== enrolment.amount_cents;
  const outstanding = enrolment
    ? Math.max((feeValid ? feeCents : enrolment.amount_cents) - enrolment.received_cents, 0)
    : 0;
  const fee: FeeCorrection | undefined = feeChanged
    ? { amount_cents: feeCents, ...(feeReason.trim() ? { reason: feeReason.trim() } : {}) }
    : undefined;
  /* A choice worth offering only when the family really has more than this one
     registration owing. */
  const hasFamilyChoice = (family?.count ?? 0) > 1;
  const chasingFamily = hasFamilyChoice && scope === "family";
  /* The family send is gated on the FAMILY's total, not this record's: the host
     may have opened a child who is already square in order to chase the
     siblings who are not. */
  const askingCents = chasingFamily ? family!.totalCents : outstanding;

  /* Switching who the email is for rewrites the SUGGESTED wording — but only
     while it is still ours. The moment the host edits a word the message is
     theirs, and having it rewritten under them by a radio button would throw
     the sentence they just wrote away. Same rule as the fee box below. */
  const onScopeChange = (next: RequestScope) => {
    setScope(next);
    if (!enrolment) return;
    const suggestion =
      next === "family" && (family?.count ?? 0) > 1
        ? defaultFamilyRequestMessage(family!.count, family!.totalCents)
        : defaultRequestMessage(
            enrolment.child_name,
            (feeValid ? feeCents : enrolment.amount_cents) - enrolment.received_cents,
          );
    if (message === suggested.current) {
      suggested.current = suggestion;
      setMessage(suggestion);
    }
  };

  const onFeeAmountChange = (value: string) => {
    setFeeAmount(value);
    if (!enrolment) return;
    const cents = Math.round(Number(value) * 100);
    if (!Number.isFinite(cents) || cents < 0) return;
    const next = defaultRequestMessage(
      enrolment.child_name,
      cents - enrolment.received_cents,
    );
    if (message === suggested.current) {
      suggested.current = next;
      setMessage(next);
    }
  };

  return (
    <Dialog open={enrolment !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ask for payment</DialogTitle>
          <DialogDescription>
            {enrolment ? (
              <>
                <strong className="text-ink">{enrolment.payment_ref}</strong> —{" "}
                {enrolment.parent_name} ({enrolment.child_name}). Fee{" "}
                {formatAud(enrolment.amount_cents)}
                {enrolment.received_cents > 0 && (
                  <>, received {formatAud(enrolment.received_cents)}</>
                )}
                . Outstanding <strong className="text-ink">{formatAud(outstanding)}</strong>.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {/* THE FEE ITSELF. A record can say "Paid" because nothing was ever
            charged — then the thing to fix is the fee, and the email follows
            from it. */}
        <div className="rounded-card border border-border bg-paper p-3">
          {feeOpen ? (
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="request-fee">
                  What should this registration be charged?
                </Label>
                <Input
                  id="request-fee"
                  inputMode="decimal"
                  className="max-w-40"
                  value={feeAmount}
                  onChange={(e) => onFeeAmountChange(e.target.value)}
                  aria-invalid={!feeValid}
                />
                <p className="text-xs text-muted">
                  {feeChanged
                    ? `Currently ${formatAud(enrolment?.amount_cents ?? 0)}. Saving this ` +
                      "re-opens the record for what's still owing — money already " +
                      "received stays exactly as it is."
                    : "Change this if the fee on the record is wrong — for example a discount that shouldn't have applied."}
                </p>
              </div>
              {feeChanged && (
                <div className="grid gap-1.5">
                  <Label htmlFor="request-fee-reason">Why? (optional)</Label>
                  <Input
                    id="request-fee-reason"
                    value={feeReason}
                    onChange={(e) => setFeeReason(e.target.value)}
                    placeholder="e.g. junior leader — the family waiver doesn't apply"
                  />
                  <p className="text-xs text-muted">
                    Kept on the record&apos;s history. Not sent to the family.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <span>
                Fee on this record: <strong>{formatAud(enrolment?.amount_cents ?? 0)}</strong>
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto text-xs text-muted hover:text-primary"
                onClick={() => setFeeOpen(true)}
              >
                That&apos;s wrong — correct it
              </Button>
            </div>
          )}
        </div>

        {/* ONE CHILD, OR THE WHOLE FAMILY.
            Only offered when there is a real choice to make. A family of three
            is one bill in the parent's head, so chasing it as three emails
            with three references is how a transfer arrives split or short —
            but the host still decides, per send. */}
        {hasFamilyChoice && (
          <div
            className="grid gap-2 rounded-card border border-border bg-paper p-3"
            role="radiogroup"
            aria-label="Who to ask"
          >
            <Label>Who should this ask?</Label>
            <label className="flex items-start gap-2.5 text-sm text-ink">
              <input
                type="radio"
                name="request-scope"
                className="mt-1 shrink-0"
                checked={scope === "one"}
                onChange={() => onScopeChange("one")}
              />
              <span>
                Just <strong>{enrolment?.child_name}</strong> —{" "}
                {formatAud(outstanding)}
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-ink">
              <input
                type="radio"
                name="request-scope"
                className="mt-1 shrink-0"
                checked={scope === "family"}
                onChange={() => onScopeChange("family")}
              />
              <span>
                The whole family — <strong>{family!.count} registrations</strong>,{" "}
                {formatAud(family!.totalCents)} in one email
                <span className="mt-0.5 block text-xs text-muted">
                  Anyone who has already paid is shown as paid and never asked again.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="request-message">What would you like to say?</Label>
          <Textarea
            id="request-message"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p className="text-xs text-muted">
            The amount owing, the reference and a <strong>Pay now</strong> link are added
            underneath automatically — you don&apos;t need to type them.
          </p>
        </div>

        <p className="rounded-card border border-border bg-paper p-3 text-sm text-ink">
          {askingCents <= 0 ? (
            <>
              {chasingFamily ? "This family has" : "This record has"}{" "}
              <strong>nothing owing</strong>, so there&apos;s nothing to ask for yet. Put the
              correct fee in above and the email goes with it.
            </>
          ) : (
            <>
              This asks <strong>{enrolment?.parent_email}</strong> for{" "}
              <strong>{formatAud(askingCents)}</strong>
              {chasingFamily && (
                <> across {family!.count} registrations, in one email</>
              )}
              {feeChanged && " — and corrects the fee first"}.
            </>
          )}
        </p>

        {Boolean(feeError) && (
          <p
            className="rounded-card border border-border bg-paper p-3 text-sm text-ink"
            role="alert"
          >
            Couldn&apos;t change the fee
            {feeError instanceof ApiError ? ` (error ${feeError.status})` : ""}. Nothing was
            emailed — please try again.
          </p>
        )}

        {Boolean(error) && (
          <p
            className="rounded-card border border-border bg-paper p-3 text-sm text-ink"
            role="alert"
          >
            Couldn&apos;t send that
            {error instanceof ApiError ? ` (error ${error.status})` : ""}. Please try again.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          {fee && (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => onSaveFee(fee)}
            >
              Just fix the fee
            </Button>
          )}
          <Button
            type="button"
            variant="accent"
            disabled={pending || !feeValid || askingCents <= 0 || message.trim() === ""}
            onClick={() =>
              onSend({ message: message.trim(), ...(fee ? { fee } : {}), scope })
            }
          >
            {pending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
              </>
            ) : fee ? (
              "Fix the fee & send it"
            ) : chasingFamily ? (
              "Send it to the family"
            ) : (
              "Send it"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Ledger + audit history for one enrolment, oldest first. When `onVoid` /
 *  `onEdit` are provided (the host portal), each ledger row gets Edit and
 *  Remove actions — the mechanism for correcting a mistaken record. The
 *  enrolment's status always follows the corrected money (paid ↔ partial ↔
 *  unpaid), so editing an amount "changes it back" safely. */
export function PaymentHistory({
  payments,
  events,
  onVoid,
  voidPendingId,
  onEdit,
}: {
  payments: PaymentReceived[];
  events: PaymentEvent[];
  onVoid?: (payment: PaymentReceived) => void;
  voidPendingId?: number | null;
  onEdit?: (paymentId: number, input: PaymentRecordInput) => void;
}) {
  const [confirmId, setConfirmId] = React.useState<number | null>(null);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editAmount, setEditAmount] = React.useState("");
  const [editMethod, setEditMethod] = React.useState<string>("bank_transfer");
  const [editNote, setEditNote] = React.useState("");

  const startEdit = (p: PaymentReceived) => {
    setConfirmId(null);
    setEditingId(p.id);
    setEditAmount((p.amount_cents / 100).toFixed(2));
    setEditMethod(
      p.method && (RECORDABLE_METHODS as readonly string[]).includes(p.method)
        ? p.method
        : p.method === "payid"
          ? "bank_transfer"
          : "other",
    );
    setEditNote(p.note ?? "");
  };

  const editAmountCents = Math.round(Number(editAmount) * 100);
  const editAmountValid = Number.isFinite(editAmountCents) && editAmountCents > 0;

  if (payments.length === 0 && events.length === 0) {
    return <p className="text-sm text-muted">No payment activity yet.</p>;
  }
  return (
    <div className="space-y-4">
      {payments.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink">Money received</h4>
          <ul className="space-y-1.5">
            {payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-baseline gap-2 text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 self-center text-success" aria-hidden="true" />
                <span className="font-medium text-ink">{formatAud(p.amount_cents)}</span>
                <span className="text-muted">
                  {METHOD_LABEL[p.method ?? ""] ?? p.method ?? "—"} ·{" "}
                  {formatDateTime(p.received_at ?? p.created_at)}
                  {p.recorded_by ? ` · by ${p.recorded_by}` : ""}
                </span>
                {(onEdit || onVoid) && editingId !== p.id && (
                  <span className="ml-auto inline-flex items-center gap-1">
                    {confirmId === p.id ? (
                      <>
                        <span className="text-xs text-muted">Remove this payment?</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="accent"
                          disabled={voidPendingId === p.id}
                          onClick={() => {
                            onVoid?.(p);
                            setConfirmId(null);
                          }}
                        >
                          {voidPendingId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            "Remove"
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmId(null)}
                        >
                          Keep
                        </Button>
                      </>
                    ) : (
                      <>
                        {onEdit && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-xs text-muted hover:text-primary"
                            onClick={() => startEdit(p)}
                          >
                            Edit
                          </Button>
                        )}
                        {onVoid && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-xs text-muted hover:text-accent"
                            disabled={voidPendingId !== null && voidPendingId !== undefined}
                            onClick={() => setConfirmId(p.id)}
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    )}
                  </span>
                )}
                {editingId === p.id && onEdit && (
                  <div className="w-full space-y-3 rounded-card border border-border bg-paper p-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="grid gap-1.5">
                        <Label htmlFor={`edit-amount-${p.id}`}>Amount (AUD)</Label>
                        <Input
                          id={`edit-amount-${p.id}`}
                          inputMode="decimal"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          aria-invalid={!editAmountValid}
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor={`edit-method-${p.id}`}>How it arrived</Label>
                        <Select value={editMethod} onValueChange={setEditMethod}>
                          <SelectTrigger id={`edit-method-${p.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RECORDABLE_METHODS.map((m) => (
                              <SelectItem key={m} value={m}>
                                {METHOD_LABEL[m]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`edit-note-${p.id}`}>Note (optional)</Label>
                      <Input
                        id={`edit-note-${p.id}`}
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder="e.g. corrected — bank shows $30 not $50"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="accent"
                        disabled={!editAmountValid}
                        onClick={() => {
                          onEdit(p.id, {
                            amount_cents: editAmountCents,
                            method: editMethod as PaymentRecordInput["method"],
                            note: editNote.trim(),
                          });
                          setEditingId(null);
                        }}
                      >
                        Save correction
                      </Button>
                    </div>
                  </div>
                )}
                {p.note && editingId !== p.id && (
                  <span className="w-full pl-6 text-xs italic text-muted">{p.note}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {events.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink">History</h4>
          <ul className="space-y-1 text-xs text-muted">
            {events.map((ev) => (
              <li key={ev.id}>
                <span className="font-medium text-ink">{EVENT_LABEL[ev.type] ?? ev.type}</span>{" "}
                — {formatDateTime(ev.at)}
                {ev.actor ? ` · ${ev.actor}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  intent: "Parent flagged payment sent",
  payment_recorded: "Payment recorded",
  payment_voided: "Payment removed (correction)",
  payment_edited: "Payment corrected",
  fee_adjusted: "Fee corrected",
  confirmed: "Confirmed paid",
  reminder_sent: "Reminder sent",
  summary_sent: "Summary emailed to admin",
  reconcile_link_sent: "Reconcile link emailed",
  payment_request_sent: "Payment request emailed to the family",
  refunded: "Refunded",
  cancelled: "Cancelled",
  withdrawn: "Removed by the parent",
};

/**
 * Magic-link sign-in gate for the reconcile pages. The emailed reconcile
 * links sign the account manager in directly; this card covers landing here
 * cold (bookmark, expired link) — same passwordless flow as /my.
 */
export function ReconcileSignIn({
  requestLink,
  redirect,
  onSignedIn,
}: {
  requestLink: (input: { email: string; redirect: string }) => Promise<{ emailed: boolean }>;
  redirect: string;
  onSignedIn: () => void;
}) {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestLink({ email: email.trim().toLowerCase(), redirect });
      setSent(true);
    } catch {
      setError("Couldn't send the sign-in link — please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Unlock the moment the emailed link is clicked in another tab.
  React.useEffect(() => {
    if (!sent) return;
    const iv = setInterval(onSignedIn, 3000);
    return () => clearInterval(iv);
  }, [sent, onSignedIn]);

  return (
    <div className="mx-auto max-w-md rounded-card border border-border bg-white p-6">
      <h2 className="font-heading text-lg font-extrabold text-ink">Sign in to reconcile</h2>
      <p className="mt-1 text-sm text-muted">
        Enter your email and we&apos;ll send you a one-time sign-in link. Only authorised
        reconciliation emails can access these pages.
      </p>
      {sent ? (
        <p className="mt-4 rounded-card border border-primary/30 bg-primary/5 p-3 text-sm text-ink">
          Check your inbox — this page unlocks automatically once you click the link.
        </p>
      ) : (
        <form className="mt-4 flex flex-col gap-3" onSubmit={submit}>
          <div className="grid gap-1.5">
            <Label htmlFor="recon-signin-email">Email</Label>
            <Input
              id="recon-signin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-accent" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="accent" disabled={busy || !email.trim()}>
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
              </>
            ) : (
              "Email me a sign-in link"
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
